import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

const CHECKS = ["manufacturer", "classification", "intended_purpose", "udi", "documents"];

export default function OperatorClassificationReview({ project, device, role, onSaved }) {
  const t = useT();
  const [review, setReview] = useState({
    status: "not_reviewed",
    checked_items: [],
    pending_documents: "",
    observations: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/projects/${project.id}/devices/${device.id}/operator-review`)
      .then(({ data }) => {
        if (active) setReview((current) => ({ ...current, ...(data || {}) }));
      })
      .catch(() => {
        if (active) setError(t("operator_review.load_error"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [project.id, device.id, t]);

  function toggleCheck(key) {
    setSaved(false);
    setReview((current) => ({
      ...current,
      checked_items: current.checked_items.includes(key)
        ? current.checked_items.filter((item) => item !== key)
        : [...current.checked_items, key],
    }));
  }

  async function save() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const { data } = await api.put(
        `/projects/${project.id}/devices/${device.id}/operator-review`,
        review,
      );
      setReview((current) => ({ ...current, ...(data || {}) }));
      setSaved(true);
      onSaved?.();
    } catch {
      setError(t("operator_review.save_error"));
    } finally {
      setSaving(false);
    }
  }

  const fields = [
    [t("operator_review.manufacturer"), device.manufacturer_name || device.manufacturer],
    [t("operator_review.class"), device.mdr_class],
    [t("operator_review.rule"), device.classification_rule],
    [t("operator_review.purpose"), device.intended_purpose],
    [t("operator_review.model"), device.model],
    ["UDI-DI", device.udi_di],
    ["Basic UDI-DI", device.basic_udi_di],
  ];

  return (
    <section className="grid-card space-y-5 p-5" data-testid="operator-classification-review">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">
          {t("operator_review.eyebrow")} · {role === "importer" ? t("role.importer") : t("role.distributor")}
        </div>
        <h2 className="mt-1 text-lg font-extrabold">{t("operator_review.title")}</h2>
        <p className="mt-2 max-w-4xl text-sm leading-relaxed text-[var(--text-2)]">
          {t("operator_review.note")}
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="operator-review-product-data">
        {fields.map(([label, value]) => (
          <div key={label} className="min-w-0 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{label}</div>
            <div className="mt-1 break-words text-sm font-semibold text-[var(--text)]">{value || "—"}</div>
          </div>
        ))}
      </div>

      <div className="rounded-sm border border-[var(--border)] p-4">
        <h3 className="text-sm font-bold">{t("operator_review.checks_title")}</h3>
        <p className="mt-1 text-xs text-[var(--text-2)]">{t("operator_review.checks_note")}</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {CHECKS.map((key) => (
            <label key={key} className="flex items-start gap-2 text-sm text-[var(--text)]">
              <input
                type="checkbox"
                checked={review.checked_items.includes(key)}
                onChange={() => toggleCheck(key)}
                disabled={loading}
                data-testid={`operator-review-check-${key}`}
                className="mt-0.5 accent-[var(--accent)]"
              />
              <span>{t(`operator_review.check.${key}`)}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-semibold text-[var(--text-2)]">
            {t("operator_review.status")}
            <select
              value={review.status}
              onChange={(event) => {
                setSaved(false);
                setReview({ ...review, status: event.target.value });
              }}
              data-testid="operator-review-status"
              className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2 py-2 text-sm text-[var(--text)]"
            >
              <option value="not_reviewed">{t("operator_review.status_pending")}</option>
              <option value="reviewed">{t("operator_review.status_reviewed")}</option>
              <option value="discrepancy">{t("operator_review.status_discrepancy")}</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-[var(--text-2)]">
            {t("operator_review.pending_docs")}
            <textarea
              value={review.pending_documents}
              onChange={(event) => {
                setSaved(false);
                setReview({ ...review, pending_documents: event.target.value });
              }}
              rows={3}
              data-testid="operator-review-pending-docs"
              className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2 py-2 text-sm text-[var(--text)]"
            />
          </label>
          <label className="text-xs font-semibold text-[var(--text-2)] sm:col-span-2">
            {t("operator_review.observations")}
            <textarea
              value={review.observations}
              onChange={(event) => {
                setSaved(false);
                setReview({ ...review, observations: event.target.value });
              }}
              rows={3}
              data-testid="operator-review-observations"
              className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2 py-2 text-sm text-[var(--text)]"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={save} disabled={loading || saving} className="btn-primary px-4 py-2 text-sm" data-testid="operator-review-save">
            {saving ? t("operator_review.saving") : t("operator_review.save")}
          </button>
          <Link to={`/devices/${device.id}`} className="text-sm font-semibold text-[var(--accent-text)] hover:underline">
            {t("operator_review.open_product")} →
          </Link>
          {saved && <span role="status" className="text-sm text-[var(--risk-success)]">{t("operator_review.saved")}</span>}
          {error && <span role="alert" className="text-sm text-[var(--risk-danger)]">{error}</span>}
          {review.updated_at && (
            <span className="text-xs text-[var(--text-2)]">{t("operator_review.last_saved")}: {review.updated_at.slice(0, 16).replace("T", " ")}</span>
          )}
        </div>
      </div>
    </section>
  );
}
